import React, { useState, useEffect } from 'react';
import { Upload, Share2, TrendingUp, DollarSign, Activity, Users, Award, Target, Zap, Calendar, Video, Heart, Brain, Flame, Moon, ChevronRight, ArrowUp, ArrowDown, Minus, Sparkles, TrendingDown, Building2, Globe, MapPin, Check } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { shareContent } from '../utils/shareUtils';

const AthletePerformance = () => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [activeView, setActiveView] = useState('health');
  const [shareSuccess, setShareSuccess] = useState(false);
  const [projectionMonths, setProjectionMonths] = useState(3);
  const [showProjection, setShowProjection] = useState(true);
  const [animatedValues, setAnimatedValues] = useState({
    readiness: 0,
    strain: 0,
    recovery: 0,
    nilValue: 0,
    velocity: 0,
    power: 0
  });

  // Current baseline values
  const currentMetrics = {
    readiness: 85,
    strain: 72,
    recovery: 91,
    nilValue: 12500,
    velocity: 89,
    power: 85
  };

  // Platform deals with projections
  const platformDeals = [
    { 
      name: 'Opendorse', 
      logo: '🟦',
      currentDeals: 2,
      projectedDeals: (months) => Math.min(2 + Math.floor(months / 2), 8),
      avgDealSize: 2250,
      status: 'connected'
    },
    { 
      name: 'Postgame', 
      logo: '🟪',
      currentDeals: 1,
      projectedDeals: (months) => Math.min(1 + Math.floor(months / 3), 5),
      avgDealSize: 3200,
      status: 'connected'
    },
    { 
      name: 'SANIL', 
      logo: '🟨',
      currentDeals: 0,
      projectedDeals: (months) => months >= 3 ? Math.min(Math.floor(months / 3), 4) : 0,
      avgDealSize: 2800,
      status: months => months >= 3 ? 'connected' : 'pending'
    },
    { 
      name: 'NILSU', 
      logo: '🟩',
      currentDeals: 1,
      projectedDeals: (months) => Math.min(1 + Math.floor(months / 4), 3),
      avgDealSize: 1800,
      status: 'connected'
    }
  ];

  // Brand partners with tiers
  const brandPartners = [
    // National Brands
    {
      name: 'Nike',
      tier: 'national',
      icon: Globe,
      currentDeal: 0,
      projectedDeal: (months) => months >= 9 ? 8000 : months >= 6 ? 5000 : 0,
      type: 'Apparel Partnership',
      startMonth: 6
    },
    {
      name: 'Gatorade',
      tier: 'national',
      icon: Globe,
      currentDeal: 0,
      projectedDeal: (months) => months >= 3 ? 3500 : 0,
      type: 'Social Campaign',
      startMonth: 3
    },
    // Mid-Market Brands
    {
      name: 'Barstool Sports',
      tier: 'mid-market',
      icon: Building2,
      currentDeal: 2000,
      projectedDeal: (months) => 2000 + (months * 200),
      type: 'Content Partnership',
      startMonth: 0
    },
    {
      name: 'Liquid I.V.',
      tier: 'mid-market',
      icon: Building2,
      currentDeal: 0,
      projectedDeal: (months) => months >= 4 ? 2500 : 0,
      type: 'Ambassador Program',
      startMonth: 4
    },
    // Local Brands
    {
      name: 'Charlottesville Auto Group',
      tier: 'local',
      icon: MapPin,
      currentDeal: 1500,
      projectedDeal: (months) => 1500 + (months * 100),
      type: 'Local Ambassador',
      startMonth: 0
    },
    {
      name: "Miller's Sports Grill",
      tier: 'local',
      icon: MapPin,
      currentDeal: 800,
      projectedDeal: (months) => 800 + (months * 50),
      type: 'Monthly Appearances',
      startMonth: 0
    }
  ];

  // Calculate projections
  const calculateProjection = (baseValue, growthRate, months) => {
    return Math.round(baseValue * Math.pow(1 + growthRate / 100, months / 12));
  };

  // Growth rates based on prescribed plans
  const planGrowthRates = {
    readiness: { conservative: 8, moderate: 12, aggressive: 18 },
    strain: { conservative: -5, moderate: -8, aggressive: -12 },
    recovery: { conservative: 6, moderate: 10, aggressive: 15 },
    nilValue: { conservative: 35, moderate: 65, aggressive: 120 },
    velocity: { conservative: 2, moderate: 4, aggressive: 6 },
    power: { conservative: 10, moderate: 18, aggressive: 25 }
  };

  // Get projected value
  const getProjectedValue = (metric, months) => {
    const planType = months <= 3 ? 'conservative' : months <= 6 ? 'moderate' : 'aggressive';
    const growthRate = planGrowthRates[metric][planType];
    return calculateProjection(currentMetrics[metric], growthRate, months);
  };

  // Calculate total NIL from all sources
  const calculateTotalNIL = (months) => {
    let total = 0;
    
    // Platform deals
    platformDeals.forEach(platform => {
      const deals = showProjection ? platform.projectedDeals(months) : platform.currentDeals;
      total += deals * platform.avgDealSize;
    });
    
    // Brand partnerships
    brandPartners.forEach(brand => {
      const dealValue = showProjection ? brand.projectedDeal(months) : brand.currentDeal;
      total += dealValue;
    });
    
    return total;
  };

  // Program adherence with projections
  const programAdherence = {
    strength: {
      current: 92,
      projected: (months) => Math.min(92 + months * 0.5, 98)
    },
    skill: {
      current: 88,
      projected: (months) => Math.min(88 + months * 0.8, 96)
    },
    recovery: {
      current: 95,
      projected: (months) => Math.min(95 + months * 0.3, 99)
    },
    nutrition: {
      current: 78,
      projected: (months) => Math.min(78 + months * 1.5, 94)
    },
    marketing: {
      current: 82,
      projected: (months) => Math.min(82 + months * 1.2, 95)
    }
  };

  // Update values when slider changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showProjection) {
        setAnimatedValues({
          readiness: getProjectedValue('readiness', projectionMonths),
          strain: getProjectedValue('strain', projectionMonths),
          recovery: getProjectedValue('recovery', projectionMonths),
          nilValue: calculateTotalNIL(projectionMonths),
          velocity: getProjectedValue('velocity', projectionMonths),
          power: getProjectedValue('power', projectionMonths)
        });
      } else {
        setAnimatedValues({
          ...currentMetrics,
          nilValue: calculateTotalNIL(0)
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [projectionMonths, showProjection]);

  // Health metrics
  const healthMetrics = [
    {
      name: 'Training Readiness',
      current: currentMetrics.readiness,
      value: animatedValues.readiness,
      icon: Brain,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      trend: showProjection ? ((animatedValues.readiness - currentMetrics.readiness) / currentMetrics.readiness * 100).toFixed(1) : 3,
      status: animatedValues.readiness >= 85 ? 'Optimal' : animatedValues.readiness >= 70 ? 'Good' : 'Fair',
      insight: showProjection ? 'Projected with plan' : 'Peak performance window'
    },
    {
      name: 'Strain Balance',
      current: currentMetrics.strain,
      value: animatedValues.strain,
      icon: Flame,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      trend: showProjection ? ((currentMetrics.strain - animatedValues.strain) / currentMetrics.strain * 100).toFixed(1) : -2,
      status: animatedValues.strain <= 70 ? 'Optimal' : animatedValues.strain <= 80 ? 'Moderate' : 'High',
      insight: showProjection ? 'Improved recovery time' : 'Good training load'
    },
    {
      name: 'Recovery Score',
      current: currentMetrics.recovery,
      value: animatedValues.recovery,
      icon: Moon,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      trend: showProjection ? ((animatedValues.recovery - currentMetrics.recovery) / currentMetrics.recovery * 100).toFixed(1) : 5,
      status: animatedValues.recovery >= 90 ? 'Excellent' : animatedValues.recovery >= 75 ? 'Good' : 'Fair',
      insight: showProjection ? 'Enhanced with program' : 'Well recovered'
    }
  ];

  // Today's activities
  const todayActivities = [
    { time: '6:00 AM', activity: 'Morning Workout', type: 'strength', duration: '45 min', impact: 'high', completed: true },
    { time: '2:00 PM', activity: 'Bullpen Session', type: 'skill', duration: '30 min', impact: 'medium', completed: true },
    { time: '4:00 PM', activity: 'Recovery', type: 'recovery', duration: '20 min', impact: 'low', completed: false }
  ];
  

  return (
    <div className={`min-h-screen transition-colors duration-200 ${'bg-[#0a0a0a] text-white'}`}>
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className=" mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold">Blake Barker</h1>
              <p className="text-gray-500 text-sm">Virginia Baseball • SLAM Score: 85</p>
            </div>
            <div className="flex gap-3">
              <button className={`${'bg-gray-800 hover:bg-gray-700 text-white'} px-4 py-2 rounded-full flex items-center gap-2 transition-all`}>
                <Video size={18} />
                <span className="hidden sm:inline">Upload</span>
              </button>
              <button
                onClick={async () => {
                  const result = await shareContent({
                    title: 'Blake Barker - Athlete Performance',
                    text: 'View Blake Barker\'s performance analytics on SLAM Sports AI',
                  });
                  if (result.success) {
                    setShareSuccess(true);
                    setTimeout(() => setShareSuccess(false), 2000);
                  }
                }}
                className={`${'bg-gray-700 text-white hover:bg-gray-600'} ${shareSuccess ? 'bg-green-600 text-white' : ''} px-4 py-2 rounded-full font-medium flex items-center gap-2 transition-all`}
              >
                {shareSuccess ? <Check size={18} /> : <Share2 size={18} />}
                <span className="hidden sm:inline">{shareSuccess ? 'Copied!' : 'Share'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SLAM Score Widget */}
      <div className=" mx-auto px-4 pt-6">
        <div className=" from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 mb-6 border border-yellow-500/20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5" />
          <div className="relative z-10 text-center">
            <h2 className="text-gray-400 text-sm font-medium tracking-wider uppercase mb-2">Your SLAM Score</h2>
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <Zap className="text-yellow-500" size={32} />
              </div>
              <div 
                className="text-8xl font-black text-yellow-500 slam-score"
                style={{
                  fontStyle: 'italic',
                  letterSpacing: '-0.02em',
                  textShadow: '0 0 30px rgba(234, 179, 8, 0.5)',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
              >
                85
              </div>
              <div className="flex items-center gap-2">
                <Zap className="text-yellow-500" size={32} />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-gray-400">Top 15% Division I</span>
              </div>
              <div className="text-gray-600">•</div>
              <div className="flex items-center gap-2">
                <ArrowUp size={14} className="text-green-500" />
                <span className="text-gray-400">+5 pts this month</span>
              </div>
              <div className="text-gray-600">•</div>
              <div className="flex items-center gap-2">
                <Target size={14} className="text-blue-500" />
                <span className="text-gray-400">Target: 92</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="mx-auto px-4 py-4">
        <div className="flex gap-1 mb-8">
          {[
            { id: 'health', label: 'Health & Performance' },
            { id: 'revenue', label: 'NIL Revenue' },
            { id: 'program', label: 'Program Adherence' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                activeView === tab.id
                  ? ('bg-gray-700 text-white')
                  : ('text-gray-400 hover:text-white bg-gray-800')
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Projection Slider */}
        <div className={`rounded-2xl p-6 mb-8 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Sparkles className="text-yellow-500" size={20} />
                Performance Projection
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Based on your prescribed training & marketing plan
              </p>
            </div>
            <button
              onClick={() => setShowProjection(!showProjection)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                showProjection
                  ? 'bg-yellow-500/20 text-yellow-500'
                  : 'bg-white text-gray-400'
              }`}
            >
              {showProjection ? 'Showing Projection' : 'Show Current Only'}
            </button>
          </div>

          {showProjection && (
            <>
              <div className="relative mb-6">
                <input
                  type="range"
                  min="0"
                  max="12"
                  value={projectionMonths}
                  onChange={(e) => setProjectionMonths(parseInt(e.target.value))}
                  className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #eab308 0%, #eab308 ${(projectionMonths / 12) * 100}%, #dfdfdf ${(projectionMonths / 12) * 100}%, #dfdfdf 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Current</span>
                  <span>3mo</span>
                  <span>6mo</span>
                  <span>9mo</span>
                  <span>1yr</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 transition-colors duration-200 ${'bg-gray-700 text-white'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Projected NIL Value</span>
                    <TrendingUp size={16} className="text-green-500" />
                  </div>
                  <div className="text-2xl font-bold text-green-500">
                    ${(animatedValues.nilValue / 1000).toFixed(1)}K
                  </div>
                  <div className="text-xs text-gray-500">
                    +{((animatedValues.nilValue - calculateTotalNIL(0)) / calculateTotalNIL(0) * 100).toFixed(0)}% from current
                  </div>
                </div>
                <div className={`rounded-xl p-4 transition-colors duration-200 ${'bg-gray-700 text-white'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Time to Goal</span>
                    <Calendar size={16} className="text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold">
                    {projectionMonths} <span className="text-sm font-normal text-gray-500">months</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {projectionMonths <= 3 ? 'Conservative' : projectionMonths <= 6 ? 'Moderate' : 'Aggressive'} timeline
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        {activeView === 'health' && (
          <div className="space-y-8">
            {/* Primary Health Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {healthMetrics.map((metric) => {
                const Icon = metric.icon;
                const isPositiveTrend = metric.name === 'Strain Balance' ? metric.trend < 0 : metric.trend > 0;
                
                return (
                  <div key={metric.name} className={`rounded-2xl p-6 transition-colors duration-200 cursor-pointer group ${'bg-gray-800 hover:bg-gray-700 text-white'}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-xl ${metric.bgColor} group-hover:scale-110 transition-transform`}>
                        <Icon className={metric.color} size={24} />
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          {isPositiveTrend ? (
                            <ArrowUp size={16} className="text-green-500" />
                          ) : !isPositiveTrend ? (
                            <ArrowDown size={16} className="text-red-500" />
                          ) : (
                            <Minus size={16} className="text-gray-500" />
                          )}
                          <span className={`text-sm ${
                            isPositiveTrend ? 'text-green-500' : !isPositiveTrend ? 'text-red-500' : 'text-gray-500'
                          }`}>
                            {Math.abs(metric.trend)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-gray-400 text-sm">{metric.name}</h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-semibold transition-all duration-700">
                          {metric.value}
                        </span>
                        <span className="text-gray-500">/ 100</span>
                      </div>
                      {showProjection && (
                        <div className={`h-1 ${'bg-gray-700'} rounded-full overflow-hidden`}>
                          <div 
                            className={`h-full bg-gradient-to-r ${'from-gray-600 to-yellow-500'} transition-all duration-700`}
                            style={{ width: `${(metric.value / 100) * 100}%` }}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          metric.status === 'Optimal' ? 'bg-green-500/20 text-green-500' :
                          metric.status === 'Excellent' ? 'bg-purple-500/20 text-purple-500' :
                          'bg-orange-500/20 text-orange-500'
                        }`}>
                          {metric.status}
                        </span>
                        <span className="text-xs text-gray-500">{metric.insight}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Today's Activities */}
            <div className={`rounded-2xl p-6 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Today's Program</h3>
              <div className="space-y-3">
                {todayActivities.map((activity, index) => (
                  <div key={index} className={`flex items-center gap-4 p-3 rounded-xl transition-colors duration-200 ${'bg-gray-800 hover:bg-gray-700 text-white'}`}>
                    <div className={`w-2 h-12 rounded-full ${
                      activity.completed ? (
                        activity.impact === 'high' ? 'bg-red-500' :
                        activity.impact === 'medium' ? 'bg-yellow-500' :
                        'bg-green-500'
                      ) : 'bg-gray-700'
                    }`} />
                    <div className="flex-1">
                      <div className={`font-medium ${!activity.completed && 'text-gray-500'}`}>
                        {activity.activity}
                      </div>
                      <div className="text-sm text-gray-500">{activity.time} • {activity.duration}</div>
                    </div>
                    {activity.completed ? (
                      <div className="text-green-500">✓</div>
                    ) : (
                      <ChevronRight size={18} className="text-gray-600" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeView === 'revenue' && (
          <div className="space-y-6">
            {/* NIL Value Overview */}
            <div className={`rounded-2xl p-8 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold">
                    ${(animatedValues.nilValue / 1000).toFixed(1)}K
                  </h2>
                  <p className={`${'text-gray-400'}`}>
                    {showProjection ? `Projected Monthly NIL (${projectionMonths}mo)` : 'Current Monthly NIL'}
                  </p>
                </div>
                {showProjection && (
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-500">
                      +{((animatedValues.nilValue - calculateTotalNIL(0)) / calculateTotalNIL(0) * 100).toFixed(0)}%
                    </div>
                    <p className={`text-sm ${'text-gray-400'}`}>projected growth</p>
                  </div>
                )}
              </div>

              {/* Revenue Growth Chart */}
              <div className={`h-48 relative mb-6 rounded-xl p-4 transition-colors duration-200 ${'bg-gray-700'}`}>
                <svg className="w-full h-full">
                  <defs>
                    <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#10B981" stopOpacity="0.8" />
                    </linearGradient>
                  </defs>
                  {/* Dynamic path based on projection */}
                  <path
                    d={showProjection ? 
                      `M 10 ${160 - (calculateTotalNIL(0) / 50000 * 140)} 
                       Q ${100 + projectionMonths * 20} ${160 - (calculateTotalNIL(projectionMonths/2) / 50000 * 140)} 
                       ${280} ${160 - (animatedValues.nilValue / 50000 * 140)}` :
                      `M 10 160 L 280 ${160 - (calculateTotalNIL(0) / 50000 * 140)}`
                    }
                    fill="none"
                    stroke="url(#revenueGradient)"
                    strokeWidth="3"
                    className="transition-all duration-1000"
                  />
                  {/* Data points */}
                  <circle
                    cx="10"
                    cy={160 - (calculateTotalNIL(0) / 50000 * 140)}
                    r="4"
                    fill="#3B82F6"
                  />
                  {showProjection && (
                    <circle
                      cx="280"
                      cy={160 - (animatedValues.nilValue / 50000 * 140)}
                      r="4"
                      fill="#10B981"
                      className="animate-pulse"
                    />
                  )}
                </svg>
              </div>
            </div>

            {/* Platform Performance */}
            <div className={`rounded-2xl p-6 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Platform Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platformDeals.map((platform) => {
                  const projectedDeals = showProjection ? platform.projectedDeals(projectionMonths) : platform.currentDeals;
                  const status = typeof platform.status === 'function' ? platform.status(projectionMonths) : platform.status;
                  const revenue = projectedDeals * platform.avgDealSize;
                  
                  return (
                    <div key={platform.name} className={`rounded-xl p-4 transition-colors duration-200 ${'bg-gray-700'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{platform.logo}</span>
                          <div>
                            <h4 className="font-semibold">{platform.name}</h4>
                            <div className={`text-xs ${
                              status === 'connected' ? 'text-green-500' : 'text-yellow-500'
                            }`}>
                              {status}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-500">
                            ${(revenue / 1000).toFixed(1)}K
                          </div>
                          <div className={`text-xs ${'text-gray-400'}`}>
                            {projectedDeals} deals × ${(platform.avgDealSize / 1000).toFixed(1)}K
                          </div>
                        </div>
                      </div>
                      {showProjection && projectedDeals > platform.currentDeals && (
                        <div className="text-xs text-blue-400">
                          +{projectedDeals - platform.currentDeals} new deals projected
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Brand Partnerships */}
            <div className={`rounded-2xl p-6 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Brand Partnerships</h3>
              <div className="space-y-3">
                {brandPartners.map((brand) => {
                  const dealValue = showProjection ? brand.projectedDeal(projectionMonths) : brand.currentDeal;
                  const isActive = dealValue > 0;
                  const Icon = brand.icon;
                  
                  return (
                    <div
                      key={brand.name}
                      className={`p-4 rounded-xl transition-all ${
                        isActive ? 
                          ('bg-gray-700 hover:bg-yellow-900/30') : 
                          ('bg-gray-800 opacity-50')
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            brand.tier === 'national' ? 'bg-blue-500/20' :
                            brand.tier === 'mid-market' ? 'bg-purple-500/20' :
                            'bg-green-500/20'
                          }`}>
                            <Icon size={20} className={
                              brand.tier === 'national' ? 'text-blue-500' :
                              brand.tier === 'mid-market' ? 'text-purple-500' :
                              'text-green-500'
                            } />
                          </div>
                          <div>
                            <h4 className="font-semibold">{brand.name}</h4>
                            <p className={`text-xs ${'text-gray-400'}`}>{brand.type}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {isActive ? (
                            <>
                              <div className="text-lg font-bold text-green-500">
                                ${(dealValue / 1000).toFixed(1)}K
                              </div>
                              {showProjection && dealValue > brand.currentDeal && (
                                <div className="text-xs text-blue-400">
                                  +${((dealValue - brand.currentDeal) / 1000).toFixed(1)}K
                                </div>
                              )}
                            </>
                          ) : (
                            <div className={`text-sm ${'text-gray-400'}`}>
                              Starts month {brand.startMonth}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeView === 'program' && (
          <div className="space-y-6">
            {/* Program Adherence Overview */}
            <div className={`rounded-2xl p-8 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h2 className="text-2xl font-bold mb-6">Program Adherence</h2>
              <div className="space-y-4">
                {Object.entries(programAdherence).map(([category, data]) => {
                  const value = showProjection ? data.projected(projectionMonths) : data.current;
                  const improvement = value - data.current;
                  
                  return (
                    <div key={category}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`capitalize ${'text-gray-400'}`}>{category}</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${
                            value >= 90 ? 'text-green-500' : value >= 75 ? 'text-yellow-500' : 'text-red-500'
                          }`}>
                            {value.toFixed(0)}%
                          </span>
                          {showProjection && improvement > 0 && (
                            <span className="text-xs text-blue-400">+{improvement.toFixed(0)}%</span>
                          )}
                        </div>
                      </div>
                      <div className={`h-3 rounded-full overflow-hidden relative ${'bg-gray-700'}`}>
                        <div
                          className={`h-full transition-all duration-700 ${
                            data.current >= 90 ? 'bg-green-600' : data.current >= 75 ? 'bg-yellow-600' : 'bg-red-600'
                          }`}
                          style={{ width: `${data.current}%` }}
                        />
                        {showProjection && improvement > 0 && (
                          <div
                            className="absolute h-full bg-gradient-to-r from-transparent to-blue-500/50 transition-all duration-700"
                            style={{ 
                              left: `${data.current}%`,
                              width: `${improvement}%` 
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className={`mt-6 p-4 rounded-xl transition-colors duration-200 ${'bg-gray-700'}`}>
                <p className={`text-sm ${'text-gray-300'}`}>
                  <span className="text-yellow-500 font-semibold">
                    {showProjection ? 'Projection Impact:' : 'Current Status:'}
                  </span> {' '}
                  {showProjection ? 
                    `Maintaining this trajectory will increase overall adherence by ${
                      Math.round(Object.values(programAdherence).reduce((sum, data) => 
                        sum + (data.projected(projectionMonths) - data.current), 0
                      ) / Object.keys(programAdherence).length)
                    }% in ${projectionMonths} months` :
                    'Strong adherence across most categories. Focus on nutrition for maximum gains.'
                  }
                </p>
              </div>
            </div>

            {/* Impact on Performance */}
            <div className={`rounded-2xl p-6 transition-colors duration-200 ${'bg-gray-800 text-white'}`}>
              <h3 className="text-lg font-semibold mb-4">Adherence → Performance Impact</h3>
              <div className="space-y-3">
                <div className={`flex justify-between items-center p-3 rounded-lg transition-colors duration-200 ${'bg-gray-700'}`}>
                  <span className={`${'text-gray-300'}`}>90%+ Strength Adherence</span>
                  <span className="text-green-500 font-semibold">+3 mph velocity</span>
                </div>
                <div className={`flex justify-between items-center p-3 rounded-lg transition-colors duration-200 ${'bg-gray-700'}`}>
                  <span className={`${'text-gray-300'}`}>85%+ Nutrition Compliance</span>
                  <span className="text-green-500 font-semibold">+8% power output</span>
                </div>
                <div className={`flex justify-between items-center p-3 rounded-lg transition-colors duration-200 ${'bg-gray-700'}`}>
                  <span className={`${'text-gray-300'}`}>95%+ Recovery Protocol</span>
                  <span className="text-green-500 font-semibold">2x faster gains</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .slam-score {
          font-family: 'Arial Black', 'Helvetica Neue', Arial, sans-serif;
          font-weight: 900;
          transform: skew(-8deg);
          display: inline-block;
        }
        @keyframes glow {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        .slam-score {
          animation: glow 2s ease-in-out infinite;
        }
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: #eab308;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.2);
          transition: all 0.2s;
        }
        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 0 6px rgba(234, 179, 8, 0.3);
        }
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #eab308;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.2);
          transition: all 0.2s;
          border: none;
        }
        .slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 0 6px rgba(234, 179, 8, 0.3);
        }
      `}</style>
    </div>
  );
};

export default AthletePerformance;